import { useLocation } from 'react-router-dom';

import dashboardIcon from '@/assets/icons/dashboard.svg';
import budgetsIcon from '@/assets/icons/budgets.svg';
import profileIcon from '@/assets/icons/profile.svg';
import commitmentIcon from '@/assets/icons/commitment.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import subscriberIcon from '@/assets/icons/subscriber.svg';

export interface BreadcrumbItem {
  title: string;
  path: string;
  icon?: string;
  isEllipsis?: boolean;
}

const AC_TYPE_LABELS: Record<string, string> = {
  department_accounting: 'Department Accounting Center',
  products_and_services: 'Products & Services Accounting Center',
  projects: 'Projects Accounting Center',
  contracts: 'Contracts Accounting Center'
};

function formatACTypeLabel(acType: string): string {
  return AC_TYPE_LABELS[acType.toLowerCase()] ?? acType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

type Crumb = BreadcrumbItem;

function single(title: string, path: string, icon?: string): Crumb[] {
  return [{ title, path, icon }];
}

function twoLevel(rootTitle: string, rootPath: string, leafTitle: string, icon?: string): Crumb[] {
  return [
    { title: rootTitle, path: rootPath, icon },
    { title: leafTitle, path: '' }
  ];
}

function threeLevel(rootTitle: string, rootPath: string, leafTitle: string, icon?: string): Crumb[] {
  return [
    { title: rootTitle, path: rootPath, icon },
    { title: '...', path: '', isEllipsis: true },
    { title: leafTitle, path: '' }
  ];
}

/**
 * Computes breadcrumbs purely from the current URL path.
 * Rules:
 *   - Root page  → single crumb (no trail)
 *   - 1 level in → Root > Child
 *   - 2+ levels  → Root > ... > Leaf
 */
export function useBreadcrumbs(): BreadcrumbItem[] {
  const { pathname } = useLocation();

  // ── DASHBOARD ────────────────────────────────────────────────────────────
  if (pathname === '/dashboard') {
    return single('Dashboard', '/dashboard', dashboardIcon);
  }

  // ── COMMITMENT ───────────────────────────────────────────────────────────
  if (pathname === '/commitment') {
    return single('Commitment', '/commitment', commitmentIcon);
  }

  // ── PROFILE ──────────────────────────────────────────────────────────────
  if (pathname === '/profile') return single('Profile', '/profile', profileIcon);
  if (pathname === '/profile/edit') return twoLevel('Profile', '/profile', 'Edit', profileIcon);
  if (pathname === '/profile/subscription') return twoLevel('Profile', '/profile', 'Subscription', profileIcon);
  if (pathname === '/profile/billing-history') return twoLevel('Profile', '/profile', 'Billing History', profileIcon);
  if (pathname === '/profile/terminology') return twoLevel('Profile', '/profile', 'Terminology', profileIcon);
  if (pathname === '/profile/payment-method') return twoLevel('Profile', '/profile', 'Payment Method', profileIcon);

  // ── INVOICE ──────────────────────────────────────────────────────────────
  if (pathname === '/invoice') return single('Invoice', '/invoice', invoiceIcon);
  if (pathname === '/invoice/create') return twoLevel('Invoice', '/invoice', 'Create', invoiceIcon);
  if (/^\/invoice\/[^/]+\/edit$/.test(pathname)) return threeLevel('Invoice', '/invoice', 'Edit', invoiceIcon);
  if (/^\/invoice\/[^/]+$/.test(pathname)) return twoLevel('Invoice', '/invoice', 'Invoice Detail', invoiceIcon);

  // ── BUDGET ROUTES ────────────────────────────────────────────────────────

  if (pathname === '/budgets') return single('Budgets', '/budgets', budgetsIcon);

  // /budgets/add
  if (pathname === '/budgets/add') return twoLevel('Budgets', '/budgets', 'Add Budget', budgetsIcon);

  // /budgets/edit/:budgetId  (edit before generic :budgetId)
  if (/^\/budgets\/edit\/[^/]+$/.test(pathname)) {
    return twoLevel('Budgets', '/budgets', 'Edit Budget', budgetsIcon);
  }

  // /budgets/:budgetId/work-activity/create
  const workActivityCreateMatch = pathname.match(/^\/budgets\/([^/]+)\/work-activity\/create$/);
  if (workActivityCreateMatch) {
    const budgetId = workActivityCreateMatch[1];
    return twoLevel('Budget', `/budgets/${budgetId}`, 'Log Work Activity', budgetsIcon);
  }

  // /budgets/:budgetId/work-activity/:workActivityId/submit-revision
  const workActivityRevisionMatch = pathname.match(/^\/budgets\/([^/]+)\/work-activity\/([^/]+)\/submit-revision$/);
  if (workActivityRevisionMatch) {
    const budgetId = workActivityRevisionMatch[1];
    return twoLevel('Budget', `/budgets/${budgetId}`, 'Submit Revision', budgetsIcon);
  }

  // /budgets/:budgetId/work-activity/:workActivityId/edit
  const workActivityEditMatch = pathname.match(/^\/budgets\/([^/]+)\/work-activity\/([^/]+)\/edit$/);
  if (workActivityEditMatch) {
    const budgetId = workActivityEditMatch[1];
    return twoLevel('Budget', `/budgets/${budgetId}`, 'Edit Work Activity', budgetsIcon);
  }

  // /budgets/:budgetId/work-activity/:workActivityId
  const workActivityDetailMatch = pathname.match(/^\/budgets\/([^/]+)\/work-activity\/([^/]+)$/);
  if (workActivityDetailMatch && workActivityDetailMatch[2] !== 'create') {
    const budgetId = workActivityDetailMatch[1];
    return twoLevel('Budget', `/budgets/${budgetId}`, 'Work Activity Detail', budgetsIcon);
  }

  // /budgets/:budgetId/work-activity
  const workActivityListMatch = pathname.match(/^\/budgets\/([^/]+)\/work-activity$/);
  if (workActivityListMatch) {
    const budgetId = workActivityListMatch[1];
    return twoLevel('Budget', `/budgets/${budgetId}`, 'Work Activity', budgetsIcon);
  }

  // /budgets/:budgetId/members/edit
  const membersEditMatch = pathname.match(/^\/budgets\/([^/]+)\/members\/edit$/);
  if (membersEditMatch) {
    const budgetId = membersEditMatch[1];
    return twoLevel('Budget', `/budgets/${budgetId}`, 'Manage Members', budgetsIcon);
  }

  // /budgets/:budgetId/members
  const membersMatch = pathname.match(/^\/budgets\/([^/]+)\/members$/);
  if (membersMatch) {
    const budgetId = membersMatch[1];
    return twoLevel('Budget', `/budgets/${budgetId}`, 'Manage Members', budgetsIcon);
  }

  // ── Bank Accounts ────────────────────────────────────────────────────────
  // Must be ordered most-specific → least-specific

  // /budgets/:budgetId/bank-accounts/:bankAccountId/edit
  if (/^\/budgets\/[^/]+\/bank-accounts\/[^/]+\/edit$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Edit', budgetsIcon);
  }

  // /budgets/:budgetId/bank-accounts/add
  if (/^\/budgets\/[^/]+\/bank-accounts\/add$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Add Bank Account', budgetsIcon);
  }

  // /budgets/:budgetId/bank-accounts/:bankAccountId
  if (/^\/budgets\/[^/]+\/bank-accounts\/[^/]+$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Bank Account Detail', budgetsIcon);
  }

  // /budgets/:budgetId/bank-accounts
  if (/^\/budgets\/[^/]+\/bank-accounts$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Bank Accounts', budgetsIcon);
  }

  // ── Accounting Centers ───────────────────────────────────────────────────

  // /budgets/:budgetId/accounting-centers/:acType/edit/:acId
  if (/^\/budgets\/[^/]+\/accounting-centers\/[^/]+\/edit\/[^/]+$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Edit', budgetsIcon);
  }

  // /budgets/:budgetId/accounting-centers/:acType/add
  if (/^\/budgets\/[^/]+\/accounting-centers\/[^/]+\/add$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Add', budgetsIcon);
  }

  // /budgets/:budgetId/accounting-centers/:acType/adjust
  if (/^\/budgets\/[^/]+\/accounting-centers\/[^/]+\/adjust$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Adjust', budgetsIcon);
  }

  // /budgets/:budgetId/accounting-centers/:acType/:acId  (detail)
  const acDetailMatch = pathname.match(/^\/budgets\/[^/]+\/accounting-centers\/([^/]+)\/([^/]+)$/);
  if (acDetailMatch) {
    const acType = acDetailMatch[1];
    return threeLevel('Budgets', '/budgets', `${formatACTypeLabel(acType)} Detail`, budgetsIcon);
  }

  // /budgets/:budgetId/accounting-centers/:acType  (list)
  const acListMatch = pathname.match(/^\/budgets\/[^/]+\/accounting-centers\/([^/]+)$/);
  if (acListMatch) {
    const acType = acListMatch[1];
    return threeLevel('Budgets', '/budgets', formatACTypeLabel(acType), budgetsIcon);
  }

  // ── Expense Categories ───────────────────────────────────────────────────

  // /budgets/:budgetId/expense-categories/edit/:ecId
  if (/^\/budgets\/[^/]+\/expense-categories\/edit\/[^/]+$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Edit', budgetsIcon);
  }

  // /budgets/:budgetId/expense-categories/add
  if (/^\/budgets\/[^/]+\/expense-categories\/add$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Add', budgetsIcon);
  }

  // /budgets/:budgetId/expense-categories/adjust
  if (/^\/budgets\/[^/]+\/expense-categories\/adjust$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Adjust', budgetsIcon);
  }

  // /budgets/:budgetId/expense-categories/:ecId  (detail)
  if (/^\/budgets\/[^/]+\/expense-categories\/[^/]+$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Expense Category Detail', budgetsIcon);
  }

  // /budgets/:budgetId/expense-categories  (list)
  if (/^\/budgets\/[^/]+\/expense-categories$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Expense Categories Overview', budgetsIcon);
  }

  // ── Revenue Categories ───────────────────────────────────────────────────

  // /budgets/:budgetId/revenue-categories/edit/:rcId
  if (/^\/budgets\/[^/]+\/revenue-categories\/edit\/[^/]+$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Edit', budgetsIcon);
  }

  // /budgets/:budgetId/revenue-categories/add
  if (/^\/budgets\/[^/]+\/revenue-categories\/add$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Add', budgetsIcon);
  }

  // /budgets/:budgetId/revenue-categories/adjust
  if (/^\/budgets\/[^/]+\/revenue-categories\/adjust$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Adjust', budgetsIcon);
  }

  // /budgets/:budgetId/revenue-categories/:rcId  (detail)
  if (/^\/budgets\/[^/]+\/revenue-categories\/[^/]+$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Revenue Category Detail', budgetsIcon);
  }

  // /budgets/:budgetId/revenue-categories  (list)
  if (/^\/budgets\/[^/]+\/revenue-categories$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Revenue Categories Overview', budgetsIcon);
  }

  // ── Invoices under a budget ──────────────────────────────────────────────

  // /budgets/:budgetId/invoices/:invoiceId
  if (/^\/budgets\/[^/]+\/invoices\/[^/]+$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Invoice Detail', budgetsIcon);
  }

  // /budgets/:budgetId/invoices
  if (/^\/budgets\/[^/]+\/invoices$/.test(pathname)) {
    return threeLevel('Budgets', '/budgets', 'Invoices', budgetsIcon);
  }

  // /budgets/:budgetId  (detail — must be last budget rule)
  if (/^\/budgets\/[^/]+$/.test(pathname)) {
    return twoLevel('Budgets', '/budgets', 'Budget Overview', budgetsIcon);
  }

  // ── ADMIN ROUTES ─────────────────────────────────────────────────────────
  if (pathname === '/admin/dashboard') return single('Admin Dashboard', '/admin/dashboard', dashboardIcon);
  if (pathname === '/admin/subscribers') return single('Admin Subscribers', '/admin/subscribers', subscriberIcon);
  if (/^\/admin\/subscribers\/[^/]+$/.test(pathname)) {
    return twoLevel('Admin Subscribers', '/admin/subscribers', 'Subscriber Detail', subscriberIcon);
  }

  // Fallback
  return single('Dashboard', '/dashboard', dashboardIcon);
}
